// Picking a customer / vendor fills GSTIN, state and due date from what the record already holds.
// Used by Credit Notes, Debit Notes, the Sales Invoice editor and the Purchase Invoice editor (SUN-282).
import { api } from './api';
import { INDIAN_STATES } from './indianStates';

const STATE_CODES = new Set(INDIAN_STATES.map((s) => s.code));

/** The state a GSTIN belongs to (its first two digits), or '' when that is not a known state code. */
export function stateFromGstin(gstin?: string | null): string {
  const code = (gstin ?? '').trim().slice(0, 2);
  return STATE_CODES.has(code) ? code : '';
}

/** A state written as a GST code ('06') or a name ('Haryana', any case) → its code; '' when unknown. */
export function stateCodeFromText(v?: string | null): string {
  const t = (typeof v === 'string' ? v : '').trim();
  if (!t) return '';
  if (STATE_CODES.has(t)) return t;
  const low = t.toLowerCase().replace(/\s+and\s+/g, ' & ');
  return INDIAN_STATES.find((s) => s.name.toLowerCase() === low)?.code ?? '';
}

/** B2B when the party has a GSTIN; else what the record already says (account segment / contact type); else B2C. */
export function partyCustomerType(p: { gstin?: string | null; segment?: string | null }): 'b2b' | 'b2c' {
  if ((p.gstin ?? '').trim()) return 'b2b';
  return p.segment === 'b2b' ? 'b2b' : 'b2c';
}

/** An address JSON ({ street | line1, line2, city, state, pincode }) as one line; a plain string stays as it is. */
export function addressText(a: unknown): string {
  if (typeof a === 'string') return a.trim();
  if (!a || typeof a !== 'object') return '';
  const o = a as Record<string, unknown>;
  return [o.street ?? o.line1 ?? o.addressLine1, o.line2 ?? o.addressLine2, o.city, o.state, o.pincode ?? o.postalCode]
    .map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x).trim() : ''))
    .filter(Boolean)
    .join(', ');
}

export interface PartyDetails {
  gstin: string;
  stateCode: string;
  /** Credit days from the account; null when the record does not say. */
  creditDays: number | null;
}

/** What an account (customer company) holds. State = its own stateCode, else the GSTIN's first 2 digits. */
export function accountDetails(account?: { gstin?: string | null; stateCode?: string | null; defaultCreditDays?: number | null } | null): PartyDetails {
  const gstin = (account?.gstin ?? '').trim();
  return {
    gstin,
    stateCode: (account?.stateCode ?? '').trim() || stateFromGstin(gstin),
    creditDays: account?.defaultCreditDays ?? null,
  };
}

/**
 * What a contact holds. A Contact has no stateCode: the state comes from its account (or the GSTIN).
 * GSTIN = the contact's own, else its account's.
 */
export function contactDetails(contact?: { gstin?: string | null; accountId?: string | null; state?: string | null; address?: unknown; account?: { id?: string; gstin?: string | null; stateCode?: string | null; defaultCreditDays?: number | null } | null } | null): PartyDetails & { accountId: string } {
  const acct = accountDetails(contact?.account);
  const own = (contact?.gstin ?? '').trim();
  const gstin = own || acct.gstin;
  const addr = (contact?.address && typeof contact.address === 'object' ? contact.address : {}) as Record<string, unknown>;
  return {
    gstin,
    // SUN-283: last fallback = the contact's own state (its state field, else its address state).
    stateCode: acct.stateCode || stateFromGstin(gstin) || stateCodeFromText(contact?.state) || stateCodeFromText(addr.state as string),
    creditDays: acct.creditDays,
    accountId: contact?.accountId || contact?.account?.id || '',
  };
}

/** today + n days as YYYY-MM-DD (local date). */
export function dueDateFromDays(days: number, from: Date = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A GST rate from a product / saved line: 0 stays 0; missing or not a number = null (the user must pick one). Never 18. */
export function parseGstRate(v: unknown): number | null {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** What a sales invoice already holds about its buyer (used when a note points at that invoice). */
export function invoiceParty(inv: { contactId?: string | null; accountId?: string | null; buyerGstin?: string | null; buyerState?: string | null; placeOfSupply?: string | null }) {
  const gstin = (inv.buyerGstin ?? '').trim();
  return {
    contactId: inv.contactId || '',
    accountId: inv.accountId || '',
    buyerGstin: gstin,
    buyerState: (inv.buyerState || inv.placeOfSupply || '').trim() || stateFromGstin(gstin),
  };
}

/** Every invoice, page by page (the list endpoint caps a page at 100). */
export async function loadAllInvoices<T = any>(): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 200; page++) {
    const res = await api.get('/invoices', { params: { page, limit: 100 } });
    const rows: T[] = res.data?.data?.invoices ?? res.data?.data ?? [];
    out.push(...rows);
    const totalPages = res.data?.pagination?.totalPages ?? 1;
    if (page >= totalPages || rows.length === 0) break;
  }
  return out;
}

/** Days from a payment-terms text such as "Net 30"; "Advance" / "COD" = 0; anything else = null. */
export function daysFromPaymentTerms(terms?: string | null): number | null {
  const t = (terms ?? '').trim();
  if (!t) return null;
  const m = /(\d+)/.exec(t);
  if (m) return Number(m[1]);
  return /^(advance|cod|cash)/i.test(t) ? 0 : null;
}

/** What a vendor (Purchase > Vendors) holds. State = its stateCode, else the GSTIN's first 2 digits. Credit days = its credit days, else its payment terms. */
export function vendorDetails(v?: { name?: string | null; gstin?: string | null; stateCode?: string | null; defaultCreditDays?: number | null; paymentTerms?: string | null } | null) {
  const gstin = (v?.gstin ?? '').trim();
  return {
    name: v?.name ?? '',
    gstin,
    stateCode: (v?.stateCode ?? '').trim() || stateFromGstin(gstin),
    creditDays: v?.defaultCreditDays ?? daysFromPaymentTerms(v?.paymentTerms),
  };
}

/** Every active vendor, page by page (the vendor list caps a page at 100) — the same list the Purchase Order uses. */
export async function loadAllVendors<T = any>(): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 200; page++) {
    const res = await api.get('/vendors', { params: { page, limit: 100 } });
    const rows: T[] = res.data?.data?.vendors ?? res.data?.data ?? [];
    out.push(...rows);
    const totalPages = res.data?.pagination?.totalPages ?? 1;
    if (page >= totalPages || rows.length === 0) break;
  }
  return out;
}

/** Every contact, 200 per page (the API default is 20 and the pickers have no paging). */
export async function loadAllContacts<T = any>(): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 200; page++) {
    const res = await api.get('/contacts', { params: { page, limit: 200 } });
    const rows: T[] = res.data?.data?.contacts ?? res.data?.data ?? [];
    out.push(...rows);
    const totalPages = res.data?.pagination?.totalPages ?? 1;
    if (page >= totalPages || rows.length === 0) break;
  }
  return out;
}

/** Every customer account, 100 per page. */
export async function loadAllCustomerAccounts<T = any>(): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 200; page++) {
    const res = await api.get('/accounts', { params: { type: 'customer', page, limit: 100 } });
    const rows: T[] = res.data?.data?.accounts ?? res.data?.data ?? [];
    out.push(...rows);
    const totalPages = res.data?.pagination?.totalPages ?? 1;
    if (page >= totalPages || rows.length === 0) break;
  }
  return out;
}
